<template>
  <SoCol v-if="showResults" :default-width="12">
    <div class="customer-search-results">
      <div class="customer-search-results__info mx-auto">
        <h4 class="mb-8 font-weight-light">
          <template v-if="noResults">No results for </template>
          <template v-else>
            <strong>{{ resultsCount }}</strong>
            {{ resultsCount > 1 ? "results" : "result" }} for
          </template>
          <strong>{{ customerSearch.query }}</strong>
        </h4>

        <SoAlert
          v-if="showLimitReachedWarning"
          type="warning"
          class="mb-6"
          text="Your search hit the maximum result limit. Some matches may be hidden."
          data-testid="search-limit-alert"
        />

        <template v-if="noResults">
          <p class="text-h5">Please try:</p>
          <ul class="text-h5 pl-6 mt-6">
            <li>Ensuring you have used the correct spelling</li>
            <li>Searching by a different term using the dropdown</li>
          </ul>
        </template>
      </div>

      <SoDataTable
        v-if="resultsCount"
        :headers="columns"
        :items="searchResults"
        :searchable="false"
        :has-pagination="false"
        show-copy-to-clipboard
        row-clickable
        @click:row="viewAccount"
      />
    </div>
  </SoCol>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCustomerSearchStore } from "@/stores/customerSearch"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import {
    SoDataTableHeader,
    SoDataTableColumnType,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import SoCol from "@/components/SoCol.vue"
  import SoAlert from "@/components/SoAlert.vue"

  const columns: SoDataTableHeader[] = [
    {
      title: "Name",
      key: "name",
    },
    {
      title: "Status",
      key: "status",
      type: SoDataTableColumnType.STATUS,
    },
    {
      title: "Account",
      key: "number",
    },
    {
      title: "Junifer Customer Id",
      key: "juniferCustomerId",
    },
    {
      title: "Supply Address",
      key: "supplyAddress",
    },
    {
      title: "Billing Address",
      key: "billingAddress",
    },
    {
      title: "From",
      key: "from",
      type: SoDataTableColumnType.DATE,
    },
    {
      title: "To",
      key: "to",
      type: SoDataTableColumnType.DATE,
    },
  ]

  export default defineComponent({
    components: {
      SoCol,
      SoDataTable,
      SoAlert,
    },
    data() {
      return {
        customerSearch: useCustomerSearchStore(),
        columns,
        pagination: { pageSize: 5 },
      }
    },
    computed: {
      showResults() {
        return (
          (this.isSuccessfulNonAccountSearch &&
            (this.canShowSingleItem || !this.hasOnlyOneMpxnResult)) ||
          this.isErrorAccountSearch
        )
      },
      isErrorAccountSearch() {
        return (
          this.customerSearch.searchTerm === "account" &&
          this.customerSearch.results?.isError
        )
      },
      isSuccessfulNonAccountSearch() {
        return (
          !!this.customerSearch.results?.isFetched &&
          this.customerSearch.searchTerm !== "account"
        )
      },
      canShowSingleItem(): boolean {
        const searchTermItem = this.customerSearch.searchTerms.find(
          (item) => item.value === this.customerSearch.searchTerm,
        )

        return searchTermItem?.listResultsForSingleItem === true
      },
      hasOnlyOneMpxnResult() {
        return (
          this.customerSearch.searchTerm === "mpxn" &&
          this.customerSearch.results?.data?.length == 1
        )
      },
      searchResults() {
        if (!this.customerSearch.results?.data.length) return []
        return this.customerSearch.results?.data.map((account) => {
          return {
            name: account.formattedName || "-",
            status: {
              value: account.status.text,
              props: { status: account.status },
            },
            number: account.number,
            juniferCustomerId: account.juniferCustomerId,
            supplyAddress: account.supplyAddress?.formattedAddress || "-",
            billingAddress: account.billingAddress?.formattedAddress || "-",
            from: account.startDate,
            to: account.endDate,
          } as SoDataTableItem
        })
      },
      noResults() {
        return (
          this.customerSearch.results?.isFetched &&
          !this.customerSearch.results?.data?.length
        )
      },
      resultsCount() {
        return this.customerSearch.results?.data?.length ?? 0
      },
      showLimitReachedWarning() {
        return this.customerSearch.limitReached && !this.noResults
      },
    },
    methods: {
      viewAccount(account: SoDataTableItem) {
        this.$router.push(`/customer/${account.number}`)
      },
    },
  })
</script>
